// TEMP-WRAPPER: headless controller for the wrapper app's native activation
// screen. The app renders the UI; this runs the kit lookup and routing.

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import {
  failMobileAppKitActivation,
  hideMobileAppKitActivation,
  onMobileAppKitActivation,
  showMobileAppKitActivation,
} from 'helpers';
import { getUnregisteredKitQuery } from 'services/kits/useUnregisteredKit';

const GENERIC_ERROR =
  'Something went wrong, please check the kit ID and try again';

const NativeKitActivation = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    const lookUp = async (kitId: string) => {
      try {
        const kit = await queryClient.fetchQuery({
          ...getUnregisteredKitQuery(kitId),
          retry: 1,
        });
        if (kit?.id && kit.partner?.portal_config?.disable_kit_activation) {
          navigate(`/partners/${kit.id}`);
          return;
        }
        if (kit?.id) {
          navigate(`/kits/activate/${kit.id}`);
          return;
        }
        failMobileAppKitActivation(GENERIC_ERROR);
      } catch (error: AxiosError | any) {
        failMobileAppKitActivation(
          error?.response?.data?.message ?? GENERIC_ERROR,
        );
      }
    };

    showMobileAppKitActivation();
    const unsubscribe = onMobileAppKitActivation(event => {
      switch (event.type) {
        case 'scan-opened':
          trackEvent('Kit Scan Opened');
          return;
        case 'scan-result':
          trackEvent('Kit Scan Result', { status: event.status });
          return;
        case 'cancelled':
          navigate(-1);
          return;
        case 'submitted':
          trackEvent('Kit ID Submitted', { method: event.method });
          lookUp(event.kitId);
      }
    });
    return () => {
      unsubscribe();
      hideMobileAppKitActivation();
    };
  }, [navigate, queryClient, trackEvent]);

  return null;
};

export default NativeKitActivation;
